<template>
	<div
		:class="[
			'border-2 rounded-lg cursor-pointer transition-all duration-200 shadow-sm',
			'p-3 sm:p-4 min-h-[44px] touch-manipulation',
			isSelected
				? 'border-blue-500 bg-blue-50'
				: 'border-gray-200 hover:border-blue-300 hover:shadow-md hover:scale-[1.01]',
			rippleEffect ? 'overflow-hidden relative' : '',
		]"
		:data-testid="dataTestId"
		:tabindex="disabled ? -1 : 0"
		:aria-selected="isSelected"
		:aria-disabled="disabled"
		role="option"
		@click="!disabled && handleSelect()"
		@keydown.space.prevent="!disabled && handleSelect()"
		@keydown.enter.prevent="!disabled && handleSelect()"
	>
		<div class="select-none">
			<slot />
		</div>

		<!-- Ripple effect container -->
		<span v-if="rippleEffect && ripples.length > 0" class="absolute inset-0 pointer-events-none overflow-hidden">
			<span
				v-for="(ripple, index) in ripples"
				:key="index"
				class="absolute rounded-full bg-blue-200 opacity-70"
				:style="{
					width: ripple.size + 'px',
					height: ripple.size + 'px',
					left: ripple.x + 'px',
					top: ripple.y + 'px',
					transform: 'translate(-50%, -50%) scale(0)',
					animation: `ripple ${rippleDuration}ms ease-out forwards`,
				}"
			/>
		</span>
	</div>
</template>

<script setup lang="ts">
import { ref } from 'vue'

interface Ripple {
	x: number
	y: number
	size: number
}

const props = defineProps({
	isSelected: {
		type: Boolean,
		default: false,
	},
	disabled: {
		type: Boolean,
		default: false,
	},
	dataTestId: {
		type: String,
		default: '',
	},
	rippleEffect: {
		type: Boolean,
		default: true,
	},
	rippleDuration: {
		type: Number,
		default: 600,
	},
})

const emit = defineEmits(['select'])

const ripples = ref<Ripple[]>([])

const handleSelect = (event?: MouseEvent) => {
	emit('select')

	if (props.rippleEffect && event instanceof MouseEvent) {
		createRipple(event)
	}
}

const createRipple = (event: MouseEvent) => {
	const element = event.currentTarget as HTMLElement
	const rect = element.getBoundingClientRect()

	const x = event.clientX - rect.left
	const y = event.clientY - rect.top

	// Calculate ripple size based on element dimensions
	const size = Math.max(rect.width, rect.height) * 2

	// Add new ripple
	const newRipple = { x, y, size }
	ripples.value.push(newRipple)

	// Remove ripple after animation completes
	setTimeout(() => {
		ripples.value = ripples.value.filter((r) => r !== newRipple)
	}, props.rippleDuration)
}
</script>

<style>
@keyframes ripple {
	to {
		transform: translate(-50%, -50%) scale(1);
		opacity: 0;
	}
}

/* Focus styles for accessibility */
[role='option']:focus-visible {
	outline: 2px solid #3b82f6;
	outline-offset: 2px;
}
</style>
